/**
 * redshift-iam-setup.ts — IAM policy JSON and passwordless-user SQL for Redshift IAM auth.
 *
 * Mirrors the RudderStack IAM role setup guide for provisioned clusters.
 * @see https://www.rudderstack.com/docs/destinations/aws-iam-role-for-rudderstack/redshift-iam-role/
 */
import { isSafeSqlIdentifier, isValidAwsRegion, isValidRedshiftClusterIdentifier } from '@server/redshift';

import { generateRedshiftRetlSchemaSQL } from './redshift-setup';

function assertIdent(name: string, label: string): string {
  const trimmed = name.trim();
  if (!isSafeSqlIdentifier(trimmed)) {
    throw new Error(`${label}: must start with a letter or underscore; only letters, numbers, underscores allowed`);
  }
  return trimmed;
}

function assertClusterIdentifier(value: string): string {
  const trimmed = value.trim();
  if (!isValidRedshiftClusterIdentifier(trimmed)) {
    throw new Error(
      'Cluster identifier: must start with a letter, may include letters, numbers, and hyphens, and cannot end with a hyphen',
    );
  }
  return trimmed;
}

/** Derive AWS partition from a region id (commercial, GovCloud, China). */
export function awsPartitionFromRegion(region: string): string {
  if (region.startsWith('cn-')) return 'aws-cn';
  if (region.startsWith('us-gov-')) return 'aws-us-gov';
  return 'aws';
}

export interface RedshiftIamPolicyParams {
  region: string;
  accountId: string;
  clusterIdentifier: string;
  dbUser: string;
  database: string;
}

/** IAM policy JSON granting GetClusterCredentials for a provisioned Redshift cluster. */
export function generateRedshiftIamPolicyJSON(params: RedshiftIamPolicyParams): string {
  const region = params.region.trim();
  const accountId = params.accountId.trim();
  const clusterIdentifier = assertClusterIdentifier(params.clusterIdentifier);
  const dbUser = assertIdent(params.dbUser, 'User');
  const database = assertIdent(params.database, 'Database');

  if (!/^\d{12}$/.test(accountId)) {
    throw new Error('AWS account ID must be a 12-digit number');
  }
  if (!isValidAwsRegion(region)) {
    throw new Error('Invalid AWS region');
  }

  const partition = awsPartitionFromRegion(region);
  const policy = {
    Version: '2012-10-17',
    Statement: [
      {
        Sid: 'GetClusterCredentials',
        Effect: 'Allow',
        Action: 'redshift:GetClusterCredentials',
        Resource: [
          `arn:${partition}:redshift:${region}:${accountId}:dbuser:${clusterIdentifier}/${dbUser}`,
          `arn:${partition}:redshift:${region}:${accountId}:dbname:${clusterIdentifier}/${database}`,
        ],
      },
    ],
  };

  return JSON.stringify(policy, null, 2);
}

export interface RedshiftIamSetupSQLParams {
  username: string;
  database: string;
  readWrite?: boolean;
}

/** Setup SQL for a passwordless IAM-authenticated database user with database-scoped grants. */
export function generateRedshiftIamSetupSQL(params: RedshiftIamSetupSQLParams): string {
  const username = assertIdent(params.username, 'Username');
  const database = assertIdent(params.database, 'Database');
  const readWrite = params.readWrite ?? false;
  const mode = readWrite ? 'Read-Write' : 'Read-Only';

  const writeStep = readWrite
    ? `
-- Step 4: Grant write access to all current and future tables
GRANT INSERT, UPDATE, DELETE
FOR TABLES IN DATABASE ${database}
TO ${username};
`
    : '';

  return `-- ============================================================
-- Rudder Lookout -- Amazon Redshift IAM ${mode} Setup
-- Requires superuser privileges. Run in the Redshift Query Editor.
-- Create the RudderStack IAM role separately (see IAM policy JSON).
-- ============================================================

-- Step 1: Create passwordless user for IAM authentication
CREATE USER ${username} PASSWORD DISABLE;

-- Step 2: Grant access to the database
GRANT USAGE
FOR SCHEMAS IN DATABASE ${database}
TO ${username};

-- Step 3: Grant read access to all current and future tables and views
GRANT SELECT
FOR TABLES IN DATABASE ${database}
TO ${username};
${writeStep}
-- Step ${readWrite ? '5' : '4'}: Reverse ETL internal schema (required for activation)
-- Granted even for a read-only account: this is Reverse ETL's own bookkeeping schema,
-- not your data. Skip it and the account explores fine, then fails at first activation.
${generateRedshiftRetlSchemaSQL(username, database)}

-- Step ${readWrite ? '6' : '5'}: Verify (run after setup)
SELECT has_schema_privilege('${username}', '_rudderstack', 'CREATE') AS can_write_retl_schema;
-- Must be true. Lookout's Test Connection checks the same thing and warns if it is not.
`;
}
